// SIWS CUSTOM (2026-09-24): Tiered Hat picker — pairs with
// sections/siws-tiered-hat-product.liquid. Reusable across hats; everything
// product-specific arrives on data-* attributes from the section settings.
//
// Rules (Michelle, 2026-09-23 emails):
//   - Percent-off tiers on the TOTAL of THIS product (all sizes combined).
//   - Minimum N hats of THIS product per order; after that, single-hat increments.
//   - Never combined with other hats (per-product, matched by product_id).
//
// Cart-aware: hats of this product already in the cart count toward the
// minimum and the tier, so a dealer topping up 12 → 24 isn't blocked or
// shown the wrong price. Matched by product_id, never by title text (RCA #1650).
//
// PDP-side preview only. The real discount is ONE Regios record per product.

(function () {
  'use strict';

  function fmt(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // "24:5,48:10,96:20" → [{min:24,pct:5},…] sorted ascending.
  function parseTiers(raw) {
    return String(raw || '')
      .split(',')
      .map(function (p) {
        var parts = p.split(':');
        return { min: parseInt(parts[0], 10), pct: parseFloat(parts[1]) };
      })
      .filter(function (t) { return !isNaN(t.min) && !isNaN(t.pct); })
      .sort(function (a, b) { return a.min - b.min; });
  }

  // Same integer-cents math as the Liquid tier box: base × (100 − pct) / 100, floored.
  function tierCents(base, pct) {
    return Math.floor((base * (100 - pct)) / 100);
  }

  class SIWSTieredHat {
    constructor(el) {
      this.el        = el;
      this.productId = String(el.getAttribute('data-product-id'));
      this.minQty    = parseInt(el.getAttribute('data-min-qty'), 10) || 0;
      this.defaultQty= parseInt(el.getAttribute('data-default-qty'), 10) || 0;
      this.tiers     = parseTiers(el.getAttribute('data-tiers'));
      try { this.variants = JSON.parse(el.getAttribute('data-variants') || '[]'); }
      catch (e) { this.variants = []; }
      this.cartQty       = 0;   // this product, all variants, already in cart
      this.cartByVariant = {};  // variant id → qty in cart (for stock caps)

      this.rows    = Array.prototype.slice.call(el.querySelectorAll('[data-th-row]'));
      this.atcBtn  = el.querySelector('[data-th-atc]');
      this.lineEl  = el.querySelector('[data-th-line]');
      this.msgEl   = el.querySelector('[data-th-message]');
      this.minEl   = el.querySelector('[data-th-min]');
      this.minTitle= el.querySelector('[data-th-min-title]');
      this.minSub  = el.querySelector('[data-th-min-sub]');
      this.tierLis = Array.prototype.slice.call(el.querySelectorAll('[data-th-tier]'));
      this.minTitleDefault = this.minTitle ? this.minTitle.textContent : '';
      this.minSubDefault   = this.minSub ? this.minSub.textContent : '';

      this._bindRows();
      this._bindAtc();
      this._bindThumbs();
      this._update();
      this._fetchCart();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this._fetchCart());
      }
    }

    _variant(id) {
      for (var i = 0; i < this.variants.length; i++) {
        if (String(this.variants[i].id) === String(id)) return this.variants[i];
      }
      return null;
    }

    // Max a dealer can still add for a variant: stock minus what's already in cart.
    _cap(id) {
      var v = this._variant(id);
      if (!v || !v.tracked) return Infinity;
      return Math.max(0, v.stock - (this.cartByVariant[String(id)] || 0));
    }

    _input(row) { return row.querySelector('[data-th-qty]'); }

    _rowQty(row) {
      var n = parseInt(this._input(row).value, 10);
      return isNaN(n) || n < 0 ? 0 : n;
    }

    _setRow(row, n) {
      var cap = this._cap(row.getAttribute('data-variant-id'));
      if (n > cap) {
        n = cap;
        this._showMsg('Only ' + cap + ' available' + (this.rows.length > 1 ? ' in ' + this._variant(row.getAttribute('data-variant-id')).title : '') + '.');
      }
      this._input(row).value = String(Math.max(0, n));
      this._update();
    }

    _bindRows() {
      this.rows.forEach((row) => {
        var minus = row.querySelector('[data-th-minus]');
        var plus  = row.querySelector('[data-th-plus]');
        var input = this._input(row);
        if (minus) minus.addEventListener('click', () => { this._clearMsg(); this._setRow(row, this._rowQty(row) - 1); });
        if (plus)  plus.addEventListener('click',  () => { this._clearMsg(); this._setRow(row, this._rowQty(row) + 1); });
        if (input) {
          input.addEventListener('input', () => { this._clearMsg(); this._update(); });
          input.addEventListener('blur',  () => this._setRow(row, this._rowQty(row)));
        }
      });
    }

    _pickerQty() {
      return this.rows.reduce((s, r) => s + this._rowQty(r), 0);
    }

    _fetchCart() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .then((cart) => {
          var byV = {};
          var total = 0;
          (cart.items || []).forEach((i) => {
            if (String(i.product_id) !== this.productId) return;
            byV[String(i.variant_id)] = (byV[String(i.variant_id)] || 0) + i.quantity;
            total += i.quantity;
          });
          this.cartByVariant = byV;
          this.cartQty = total;
          this._update();
        })
        .catch(() => { /* keep last known cart state */ });
    }

    _tierFor(total) {
      var hit = null;
      this.tiers.forEach((t) => { if (total >= t.min) hit = t; });
      return hit; // null = base price
    }

    _update() {
      var picker = this._pickerQty();
      var total  = picker + this.cartQty;
      var tier   = this._tierFor(total);
      var base   = this.variants.length ? this.variants[0].price : 0;
      var each   = tier ? tierCents(base, tier.pct) : base;

      // Highlight the tier row the whole order lands in.
      var activeKey = tier ? String(tier.min) : '0';
      this.tierLis.forEach((li) => {
        li.classList.toggle('siws-th__tier--active', total > 0 && li.getAttribute('data-th-tier') === activeKey);
      });

      // Plus buttons respect stock.
      this.rows.forEach((row) => {
        var plus = row.querySelector('[data-th-plus]');
        var v = this._variant(row.getAttribute('data-variant-id'));
        if (plus && v && v.available) plus.disabled = this._rowQty(row) >= this._cap(v.id);
      });

      // Minimum box: red until met (counting cart), then green.
      var short = this.minQty > 0 ? Math.max(0, this.minQty - total) : 0;
      if (this.minEl) {
        var met = this.minQty > 0 && total >= this.minQty;
        this.minEl.classList.toggle('siws-th__min--met', met);
        if (this.minTitle) {
          this.minTitle.textContent = met
            ? '✓ ' + this.minQty + '-hat minimum met'
            : this.minTitleDefault;
        }
        if (this.minSub) {
          this.minSub.textContent = (!met && total > 0)
            ? 'Add ' + short + ' more to reach the ' + this.minQty + '-hat minimum. Mix and match any sizes.'
            : this.minSubDefault;
        }
      }

      // One-line price summary.
      if (this.lineEl) {
        if (picker > 0) {
          var txt = picker + ' hat' + (picker === 1 ? '' : 's') + ' × <strong>' + fmt(each) + '</strong> = <strong>' + fmt(each * picker) + '</strong>';
          if (tier) txt += ' (' + tier.pct + '% off)';
          if (this.cartQty > 0) txt += '<br><small>Priced with the ' + this.cartQty + ' already in your cart.</small>';
          this.lineEl.innerHTML = txt;
          this.lineEl.hidden = false;
        } else {
          this.lineEl.hidden = true;
          this.lineEl.innerHTML = '';
        }
      }

      // ATC: needs ≥1 selected AND the order (cart + picker) at the minimum.
      if (this.atcBtn && !this._adding) {
        if (picker <= 0) {
          this.atcBtn.disabled = true;
          this.atcBtn.textContent = this.rows.length > 1 ? 'Choose your sizes' : 'Choose a quantity';
        } else if (short > 0) {
          this.atcBtn.disabled = true;
          this.atcBtn.textContent = 'Add ' + short + ' more to reach ' + this.minQty;
        } else {
          this.atcBtn.disabled = false;
          this.atcBtn.textContent = 'Add to Cart';
        }
      }
    }

    _bindAtc() {
      if (!this.atcBtn) return;
      this.atcBtn.addEventListener('click', () => {
        var items = this.rows
          .map((r) => ({ id: Number(r.getAttribute('data-variant-id')), quantity: this._rowQty(r) }))
          .filter((i) => i.quantity > 0);
        if (!items.length) return;

        this._clearMsg();
        this._adding = true;
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        })
          .then((r) => r.json().then((body) => ({ ok: r.ok, body: body })))
          .then(({ ok, body }) => {
            this._adding = false;
            if (!ok) {
              this._showMsg(body.description || body.message || 'Could not add to cart.');
              this._fetchCart();
              return;
            }
            // Reset: multi-size back to 0s; single back to 0 (min already satisfied by cart).
            this.rows.forEach((r) => { this._input(r).value = '0'; });
            this._showMsg('✓ Added to cart.', true);
            this._fetchCart();
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'siws-tiered-hat', cartData: body });
            }
          })
          .catch(() => {
            this._adding = false;
            this._showMsg('Could not add to cart. Please try again.');
            this._update();
          });
      });
    }

    _bindThumbs() {
      var hero = this.el.querySelector('[data-th-hero]');
      if (!hero) return;
      this.el.querySelectorAll('[data-th-thumb]').forEach((b) => {
        b.addEventListener('click', () => { hero.src = b.getAttribute('data-src'); });
      });
    }

    _showMsg(text, ok) {
      if (!this.msgEl) return;
      this.msgEl.textContent = text;
      this.msgEl.style.color = ok ? '#15803d' : '';
      this.msgEl.style.background = ok ? '#f0fdf4' : '';
      this.msgEl.style.borderColor = ok ? '#bbf7d0' : '';
      this.msgEl.hidden = false;
    }
    _clearMsg() { if (this.msgEl) this.msgEl.hidden = true; }
  }

  function init() {
    document.querySelectorAll('.siws-th__container').forEach((el) => new SIWSTieredHat(el));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
